import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type * as PanelTypes from '../../state/views/panel/types';
import type * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {useRampFlagOverlayWorkspaceDrawer} from '../../util/rampFeatureFlags';
import {
  INLINE_DRAWER_MIN_PANEL_BANK_WIDTH,
  INLINE_WORKSPACE_DRAWER_DURATION,
} from './constants';

export type DrawerContentType =
  | 'add-panels'
  | 'create-trigger'
  | 'panels-quick-add'
  | 'section-settings'
  | 'workspace-settings'
  | 'media-settings'
  | 'run-issues'
  | 'line-plot-editor'
  | undefined;

export type WorkspaceDrawerContextType = {
  contentType: DrawerContentType;
  delayedContentType: DrawerContentType;
  drawerType: 'inline' | 'floating';
  shouldHidePanelContents: boolean;
  stablePanelBankWidth: number;
  targetSectionRef?: PanelBankSectionConfigTypes.Ref;
  closeWorkspaceDrawer: (shouldAnimate?: boolean) => void;
  openWorkspaceDrawer: (
    type: DrawerContentType,
    sectionRef?: PanelBankSectionConfigTypes.Ref,
    panelRef?: PanelTypes.Ref
  ) => void;
  targetPanelRef?: PanelTypes.Ref;
  toggleWorkspaceDrawer: (
    type: DrawerContentType,
    sectionRef?: PanelBankSectionConfigTypes.Ref,
    panelRef?: PanelTypes.Ref
  ) => void;
};

export const WorkspaceDrawerContext = createContext<
  WorkspaceDrawerContextType | undefined
>(undefined);

type WorkspaceDrawerProps = {
  panelBankWidth: number;
  children: React.ReactNode;
};

/**
 * This context provider is used to control the drawer that slides in and out
 * in a workspace page.
 */
export const WorkspaceDrawerContextProvider = ({
  panelBankWidth,
  children,
}: WorkspaceDrawerProps) => {
  const [contentType, setContentType] = useState<DrawerContentType>();
  const [delayedContentType, setDelayedContentType] =
    useState<DrawerContentType>();

  const [targetSectionRef, setTargetSectionRef] = useState<
    PanelBankSectionConfigTypes.Ref | undefined
  >();

  const [targetPanelRef, setTargetPanelRef] = useState<
    PanelTypes.Ref | undefined
  >();

  const openWorkspaceDrawer = useCallback(
    (
      type: DrawerContentType,
      sectionRef?: PanelBankSectionConfigTypes.Ref,
      panelRef?: PanelTypes.Ref
    ) => {
      setContentType(type);
      setTargetSectionRef(sectionRef);
      setTargetPanelRef(panelRef);
    },
    []
  );

  const closeWorkspaceDrawer = useCallback(() => {
    setContentType(undefined);
  }, []);

  useEffect(() => {
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (contentType) {
          closeWorkspaceDrawer();
        }
      }
    }
    window.addEventListener('keydown', handleEscapeKey);
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [closeWorkspaceDrawer, contentType]);

  const toggleWorkspaceDrawer = useCallback(
    (
      newContentType: DrawerContentType,
      newTargetSectionRef?: PanelBankSectionConfigTypes.Ref,
      newPanelRef?: PanelTypes.Ref
    ) => {
      if (
        newContentType !== contentType ||
        newTargetSectionRef?.id !== targetSectionRef?.id ||
        newPanelRef?.id !== targetPanelRef?.id
      ) {
        openWorkspaceDrawer(newContentType, newTargetSectionRef, newPanelRef);
      } else {
        closeWorkspaceDrawer();
      }
    },
    [
      contentType,
      targetSectionRef?.id,
      targetPanelRef?.id,
      openWorkspaceDrawer,
      closeWorkspaceDrawer,
    ]
  );

  const stablePanelBankWidth = useStablePanelBankWidth({
    contentType,
    closeWorkspaceDrawer,
    panelBankWidth,
  });

  // This is a flag to always show overlay workspace drawer since inline is slouggish on large workspaces
  const shouldUseOverlayWorkspaceDrawer = useRampFlagOverlayWorkspaceDrawer();
  const drawerType: 'inline' | 'floating' = useMemo(() => {
    if (shouldUseOverlayWorkspaceDrawer) {
      return 'floating';
    }
    return stablePanelBankWidth > INLINE_DRAWER_MIN_PANEL_BANK_WIDTH
      ? 'inline'
      : 'floating';
  }, [stablePanelBankWidth, shouldUseOverlayWorkspaceDrawer]);

  // Delay state updates to account for open/close animation
  useEffect(() => {
    const timeout = setTimeout(
      () => {
        setDelayedContentType(contentType);
        if (!contentType) {
          setTargetSectionRef(undefined);
          setTargetPanelRef(undefined);
        }
      },
      INLINE_WORKSPACE_DRAWER_DURATION + 100 // HAX: a buffer helps with jumpiness
    );

    return () => clearTimeout(timeout);
  }, [contentType]);

  const shouldHidePanelContents = useMemo(() => {
    // we don't need to animate for floating drawer because
    // we're not resizing panelBankWidth
    if (drawerType === 'floating') {
      return false;
    }

    return !!(
      (contentType && !delayedContentType) ||
      (delayedContentType && !contentType)
    );
  }, [contentType, delayedContentType, drawerType]);

  const value = useMemo(
    () => ({
      closeWorkspaceDrawer,
      contentType,
      delayedContentType,
      drawerType,
      openWorkspaceDrawer,
      shouldHidePanelContents,
      stablePanelBankWidth,
      targetSectionRef,
      targetPanelRef,
      toggleWorkspaceDrawer,
    }),
    [
      closeWorkspaceDrawer,
      contentType,
      delayedContentType,
      drawerType,
      openWorkspaceDrawer,
      shouldHidePanelContents,
      stablePanelBankWidth,
      targetSectionRef,
      targetPanelRef,
      toggleWorkspaceDrawer,
    ]
  );

  return (
    <WorkspaceDrawerContext.Provider value={value}>
      {children}
    </WorkspaceDrawerContext.Provider>
  );
};

export const useMaybeWorkspaceDrawerContext = () => {
  return useContext(WorkspaceDrawerContext);
};

export const useWorkspaceDrawerContext = (): WorkspaceDrawerContextType => {
  const value = useContext(WorkspaceDrawerContext);

  if (value == null) {
    throw new Error(
      'useWorkspaceDrawerContext must be used within a WorkspaceDrawerContextProvider'
    );
  }

  return value;
};

export const useWorkspaceDrawerContextMaybe = () => {
  return useContext(WorkspaceDrawerContext);
};

export const useStablePanelBankWidth = ({
  contentType,
  closeWorkspaceDrawer,
  panelBankWidth,
}: {
  contentType: DrawerContentType | undefined;
  closeWorkspaceDrawer: (shouldAnimate?: boolean) => void;
  panelBankWidth: number;
}) => {
  // Inline drawer directly affects panelBankWidth, so we only want to update the
  // value to use when resizing is complete
  const [stablePanelBankWidth, setStablePanelBankWidth] = useState(0);

  useEffect(() => {
    // initialize panelBankWidth
    if (!stablePanelBankWidth) {
      setStablePanelBankWidth(panelBankWidth);
    }
  }, [panelBankWidth, stablePanelBankWidth]);

  const handleResize = useCallback(() => {
    if (contentType !== undefined) {
      // If run selector browser is resizing and drawer is open, force the drawer to close
      // to avoid jumpiness
      closeWorkspaceDrawer();
    }
    setStablePanelBankWidth(panelBankWidth);
  }, [contentType, closeWorkspaceDrawer, panelBankWidth]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  return stablePanelBankWidth;
};
